import React, { memo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { mapOptionsListStyleToCSS } from '../../../../utils/styleMapper';
import { Image } from '../../Image';
import { OptionsItemText } from '../OptionsItemText';
import type { OptionsItemVariantProps } from './types';
import { getListImagePresentation } from './imageLayout';
import { getOptionItemA11yProps } from '../../../../utils/optionItemA11y';
import styles from '../OptionsItem.module.css';

export const ImageTitleSubtitle = memo(function ImageTitleSubtitle({
  item,
  index,
  locale,
  variables,
  selectable,
  selected,
  disabled,
  selectionMode,
  appearance,
  selectedAppearance,
  onSelect,
  className,
  style,
  'data-testid': testId,
  editPath,
  previewMode,
}: OptionsItemVariantProps): React.ReactElement {
  const handleClick = useCallback(() => {
    if (selectable && !disabled && onSelect) {
      onSelect(item, index, !selected);
    }
  }, [selectable, disabled, onSelect, item, index, selected]);

  const activeStyle = selected ? selectedAppearance : appearance;
  const itemStyle: CSSProperties = mapOptionsListStyleToCSS(activeStyle);
  const imagePresentation = getListImagePresentation(item.image);

  return (
    <div
      className={`${styles.listItem}${selected ? ` ${styles.selected}` : ''}${disabled ? ` ${styles.disabled}` : ''}${className ? ` ${className}` : ''}`}
      style={style ? { ...itemStyle, ...style } : itemStyle}
      onClick={selectable && !disabled ? handleClick : undefined}
      {...getOptionItemA11yProps({ selectable, selected, disabled, selectionMode, onActivate: handleClick })}
      data-testid={testId}
      data-edit-path={editPath}
    >
      <div className={styles.asset} style={imagePresentation.assetStyle}>
        <Image image={item.image} locale={locale} fillContainer={imagePresentation.fillContainer} previewMode={previewMode} editPath={editPath ? `${editPath}.image` : undefined} />
      </div>
      <div className={styles.content}>
        <OptionsItemText label={item.title} locale={locale} variables={variables} editPath={editPath ? `${editPath}.title` : undefined} />
        <OptionsItemText label={item.subtitle} locale={locale} variables={variables} editPath={editPath ? `${editPath}.subtitle` : undefined} />
      </div>
    </div>
  );
});

ImageTitleSubtitle.displayName = 'ImageTitleSubtitle';
